import { useEffect, useState } from 'react';
import { getClickToCallWithKeyOrUrl, trackView, useTracking } from '@vfit/shared/data-access';
import { API, getFromLocalStorageByKey } from '@vfit/shared/data-access';
import { EMethodCode, ICommonData, IOptionalText, ISIAFlowType } from '@vfit/shared/models';
import {
  CONSUMER_CMS_SHOPPING_CART,
  useCmsConfig,
  IPayments,
  productSpecificError,
  SELECTED_PAYMENT,
  IGenericErrorCMS,
  getCurrentUserType,
  IUserType,
  IGlobalConfigCMS,
  GetCustomerResponse,
  IFindCallerResponse,
} from '@vfit/consumer/data-access';
import { CheckboxCards } from '@vfit/consumer/components';
import { WarningInfo } from '@vfit/shared/components';
import { organizeCheckBoxPayment, organizePaymentMethodError } from './recurrentPaymentCard.utils';
import { useCheckout } from '../../../iBuyFixed.context';
import { ID_FLOWS } from '../../checkout.constants';
import TaggingRecurrentPaymentCard from './recurrentPaymentCard.tagging';
import { useQueryClient } from 'react-query';

const RecurrentPaymentCard = ({ handleOnChangeEnableGoNext, handleGoPrevSlide }: ICommonData) => {
  const {
    currentStepKey,
    checkoutErrors,
    product,
    isModalButtonSticky,
    authorizeFlow,
    customerFlow,
    setIsLastCard,
    setHideStickyBar,
    setIsDisabledBackButton,
    setAuthorizeFlow,
    setCurrentStepKey,
  } = useCheckout();
  const queryClient = useQueryClient();
  const { authorize } = authorizeFlow;
  const { billingAccount } = customerFlow;
  const { availableNewPayMeans } = billingAccount?.data || {};
  const [selectedItem, setSelectedItem] = useState<IOptionalText>();
  const { buttonPaymentError, urlPaymentErrorCallMeNow } = productSpecificError(product);
  const paymentCMS = useCmsConfig(CONSUMER_CMS_SHOPPING_CART, API.CMS_GET_PAYMENTS) as IPayments;
  const genericError = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_GENERIC_ERROR
  ) as IGenericErrorCMS;

  // region NEW MOP enabler
  const cmsGlobalConfig = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_GLOBAL_CONFIG
  ) as IGlobalConfigCMS;
  const customerData: GetCustomerResponse[] = getFromLocalStorageByKey('customerData');
  const findCaller: IFindCallerResponse = getFromLocalStorageByKey('findCaller');
  const customerDataCF =
    customerData?.[0]?.owningIndividual?.fiscalCode || findCaller?.individualRef?.fiscalCode;
  const isGlobalBsNewMopFixed = cmsGlobalConfig?.globalconfig?.newMop?.enableNewMopFixed === 'true';

  const isCustomerXBS = customerDataCF?.includes('XBS');
  const isNewMopEnabled = isCustomerXBS || isGlobalBsNewMopFixed;

  // end region

  const isTNPUser =
    (getCurrentUserType() === IUserType.NEXT_USER_ACTIVE &&
      billingAccount?.data?.hasPendingDeviceWithInstallments) ||
    !isNewMopEnabled;
  const cmsWarningPayment = {
    title: paymentCMS?.paymentsmobile?.title || 'Grazie!',
    subtitle: 'Stiamo elaborando il tuo ordine',
    description:
      paymentCMS?.paymentsmobile?.description ||
      "Per completare l'operazione potrebbe volerci qualche istante. Per favore attendi qui, senza lasciare la pagina.",
  };
  const { title, items, description, modal } = organizeCheckBoxPayment(
    paymentCMS,
    availableNewPayMeans?.filter((el) => el !== 'wallet') || [],
    isTNPUser
  );
  const { titlePaymentError, messagePaymentError } = organizePaymentMethodError(paymentCMS);

  const checkErrors = () => {
    const action = () => {
      if (handleGoPrevSlide) handleGoPrevSlide();
    };
    checkoutErrors?.showAndTrackError?.(
      genericError,
      action,
      authorize?.error || undefined,
      titlePaymentError,
      messagePaymentError,
      action,
      'Chiudi',
      () =>
        getClickToCallWithKeyOrUrl(
          queryClient,
          urlPaymentErrorCallMeNow,
          product,
          action,
          currentStepKey,
          authorize?.error
        ),
      buttonPaymentError,
      isModalButtonSticky,
      true
    );
  };

  const handleStartPaymentFlow = (event: Event) => {
    event?.stopPropagation();
    trackView({
      event_name: 'ui_interaction',
      event_label_track: selectedItem?.title || selectedItem?.value || '',
      event_category: 'users',
      event_action: 'click',
      tracking_type: 'link',
    });
    let methodCode = selectedItem?.value;
    if (selectedItem?.value === 'paypal') methodCode = EMethodCode?.PAYPAL;
    setAuthorizeFlow({
      ...authorizeFlow,
      selectedMethod: (methodCode || '') as EMethodCode,
      siaFlowType: ISIAFlowType.RECURRENT,
      authorizationOperationMethod: 'capture',
      isStartAuthorizeFlow: true,
    });
  };

  const submitOrder = () => {
    setTimeout(() => {
      const goNextButton = document.getElementById('sticky-offer-next');
      goNextButton?.removeEventListener('click', handleStartPaymentFlow);
      if (goNextButton?.firstChild) {
        (goNextButton?.firstChild as HTMLButtonElement)?.click();
      }
    }, 800);
  };

  const checkLoading = () => {
    setIsDisabledBackButton(authorize?.isLoading);
    setHideStickyBar(authorize?.isLoading);
  };

  const onOpenModal = () => {
    trackView({
      event_name: 'ui_interaction',
      event_label_track: modal?.titleModal || '',
      event_category: 'users',
      event_action: 'click',
      tracking_type: 'link',
    });
  };

  useEffect(() => {
    if (selectedItem) {
      if (authorize?.isError) {
        checkErrors();
      }
      checkLoading();
      if (authorize?.isSuccess) {
        submitOrder();
      }
    }
  }, [authorize]);

  useEffect(() => {
    authorizeFlow?.resetReserveAuthorization?.();
    const goNextButton = document.getElementById('sticky-offer-next');
    handleOnChangeEnableGoNext?.(!!selectedItem?.value);
    if (!selectedItem?.value) {
      localStorage.removeItem(SELECTED_PAYMENT);
    } else {
      localStorage.setItem(SELECTED_PAYMENT, selectedItem?.value);
    }
    goNextButton?.addEventListener('click', handleStartPaymentFlow);
    return () => {
      goNextButton?.removeEventListener('click', handleStartPaymentFlow);
    };
  }, [selectedItem]);

  useEffect(() => {
    setIsLastCard(true);
    setCurrentStepKey(ID_FLOWS.RECURRENT_PAYMENT_CARD);
    return () => {
      setIsLastCard(false);
    };
  }, []);

  return (
    <>
      <TaggingRecurrentPaymentCard />
      {authorizeFlow?.authorize?.isLoading && <WarningInfo {...cmsWarningPayment} />}
      {!authorizeFlow?.authorize?.isLoading && (
        <CheckboxCards
          items={items}
          title={title}
          description={description}
          bottomModal={modal}
          onOpeneModal={onOpenModal}
          setSelectedItem={setSelectedItem}
          selectedItem={selectedItem}
        />
      )}
    </>
  );
};

export default RecurrentPaymentCard;

